import { gofarService } from './gofar-service';
import { HTTP_METHODS, HTTP_STATUS_CODES } from './config';
import { CHARGEBEE_ADD_ONS, CHARGEBEE_SUBSCRIPTION_PLANS, MAX_ALLOWED_QUANTITY, PRODUCT_IMAGES } from './constants';
import { STATES } from './data/address-data-provider';
import * as helper from './helper';

export const mapToSubscriptionProducts = (selectedProducts, countrySetting, coupon, quantity, selectedPlan) => {
  let isDiscountAppliedAlready = false;
  const subscriptionProducts = selectedProducts.map((product) => {
    const chargebeeProduct = CHARGEBEE_ADD_ONS[countrySetting.currencyCode].find((chargebeeProduct) =>
      chargebeeProduct.id.startsWith(product)
    );
    const originalPrice = chargebeeProduct.price;
    const isDiscountApplicableForProduct = helper.checkIfDiscountIsApplicableForProduct(
      selectedPlan,
      product,
      coupon,
      isDiscountAppliedAlready
    );
    // only one add_ons will be selected while creating coupon making sure that this process doesn't apply same coupon on both adapter and ray
    const discountedPrice = isDiscountApplicableForProduct
      ? helper.calculatePriceWithDiscount(originalPrice, coupon, countrySetting)
      : originalPrice;
    isDiscountAppliedAlready = discountedPrice < originalPrice;
    return {
      id: chargebeeProduct.id,
      name: chargebeeProduct.name,
      image: chargebeeProduct.imageUrl,
      quantity,
      originalPrice,
      price: discountedPrice
    };
  });
  return subscriptionProducts;
};

export const mapToSubscriptionPlan = (selectedPlan, countrySetting, coupon, quantity, isDiscountAppliedOnProducts) => {
  const chargebeePlan = CHARGEBEE_SUBSCRIPTION_PLANS[countrySetting.currencyCode].find((plan) =>
    plan.id.startsWith(selectedPlan)
  );
  const originalPrice = chargebeePlan.price;
  let discountedPrice = chargebeePlan.price;
  if (coupon.isValid && coupon.item_constraints) {
    // we currently do not offer discount that is applicable to plan, but this is for future purpose
    const itemConstraintsForPlan = coupon.item_constraints.find((constraint) => constraint.item_type === 'plan');
    if (itemConstraintsForPlan) {
      let isDiscountApplicableToPlan =
        itemConstraintsForPlan.constraint === 'all' ||
        (itemConstraintsForPlan.constraint === 'specific' &&
          itemConstraintsForPlan.item_price_ids.includes(chargebeePlan.id));
      discountedPrice =
        !isDiscountAppliedOnProducts && isDiscountApplicableToPlan
          ? helper.calculatePriceWithDiscount(originalPrice, coupon, countrySetting)
          : originalPrice;
    }
  }
  const subscriptionPlan = {
    id: chargebeePlan.id,
    name: chargebeePlan.name,
    image: PRODUCT_IMAGES.SUBSCRIPTION,
    quantity,
    originalPrice,
    price: discountedPrice
  };
  return subscriptionPlan;
};

export const getDefaultShipmentOption = (countrySetting) => {
  const shippingOptionForCountry = countrySetting.shippingOptions;
  return shippingOptionForCountry[0];
};

export const mapToSelectedShippingOption = (countrySetting, selectedShippingId) => {
  const shippingOptionForCountry = countrySetting.shippingOptions;
  const shippingOption = shippingOptionForCountry.find((shippingOption) => shippingOption.id === selectedShippingId);
  const chargebeeShippingOption = shippingOption || shippingOptionForCountry[0];
  const originalShippingPrice = chargebeeShippingOption.price;
  // discount will not be applicable in shipping
  const selectedShippingOption = {
    id: chargebeeShippingOption.id,
    name: chargebeeShippingOption.name,
    price: originalShippingPrice
  };
  return selectedShippingOption;
};

export const mapToSubscriptionCheckout = (
  selectedPlan,
  products,
  coupon,
  countrySetting,
  selectedQuantity,
  selectedShippingOption
) => {
  // TODO: should be able to have different quantities for Subscription, Ray and Adapter in future
  const quantity =
    parseInt(selectedQuantity, 10) > MAX_ALLOWED_QUANTITY ? MAX_ALLOWED_QUANTITY : parseInt(selectedQuantity, 10);
  const productsTotalAmount = products.reduce((total, product) => total + product.originalPrice * product.quantity, 0);
  const productsTotalDiscountedAmount = products.reduce(
    (total, product) => total + product.price * product.quantity,
    0
  );

  const isDiscountAppliedOnProducts = productsTotalDiscountedAmount < productsTotalAmount;

  const plan = mapToSubscriptionPlan(selectedPlan, countrySetting, coupon, quantity, isDiscountAppliedOnProducts);
  const isDiscountAppliedOnPlan = plan.price < plan.originalPrice;

  const planTotalAmount = plan.originalPrice * plan.quantity;

  const originalSubTotal = planTotalAmount + productsTotalAmount;
  const totalDiscountedAmount = helper.getTotalDiscountedAmount(plan, products, isDiscountAppliedOnPlan);

  const originalSubTotalWithDiscount = originalSubTotal - totalDiscountedAmount;

  const discountedSubTotal =
    coupon.apply_on === 'invoice_amount'
      ? helper.calculatePriceWithDiscount(originalSubTotal, coupon, countrySetting)
      : originalSubTotal;

  const shippingPrice = selectedShippingOption ? selectedShippingOption.price : 0;

  const grandTotal =
    coupon.apply_on === 'invoice_amount'
      ? discountedSubTotal + shippingPrice
      : originalSubTotalWithDiscount + shippingPrice;

  const result = {
    plan,
    quantity,
    originalSubTotal,
    originalSubTotalWithDiscount,
    subTotal: discountedSubTotal,
    grandTotal,
    coupon
  };
  return result;
};

export const validateCouponCode = async (couponCode, planId, productIds) => {
  const couponCodeErrorMessage = couponCode == '' ? '' : 'Invalid Coupon Code.';
  if (!couponCode || couponCode.toLowerCase().includes('grandfathered') || planId.includes('basic')) {
    return {
      isValid: false,
      message: couponCodeErrorMessage,
      code: couponCode
    };
  }

  const coupon_result = await gofarService(`/api/checkout/V2/validate-couponcode`, HTTP_METHODS.POST, {
    couponCode: couponCode
  });
  const coupon = coupon_result.data;
  // coupon status: active | expired | archived | deleted | future
  if (coupon.isValid === false || coupon.status != 'active') {
    return {
      isValid: false,
      message: couponCodeErrorMessage,
      code: couponCode
    };
  }

  // is valid for any of the product
  const itemConstraintsForCharge = coupon.item_constraints.find((constraint) => constraint.item_type === 'charge');
  if (itemConstraintsForCharge) {
    for (let i = 0; i < productIds.length; i += 1) {
      const isDiscountApplicableToCharge =
        itemConstraintsForCharge.constraint === 'all' ||
        (itemConstraintsForCharge.constraint === 'specific' &&
          itemConstraintsForCharge.item_price_ids.includes(productIds[i]));
      if (isDiscountApplicableToCharge) {
        return {
          isValid: true,
          ...coupon_result.data,
          code: couponCode
        };
      }
    }
  }

  // is valid for plan
  const itemConstraintsForPlan = coupon.item_constraints.find((constraint) => constraint.item_type === 'plan');
  const isDiscountApplicableToPlan =
    itemConstraintsForPlan.constraint === 'all' ||
    (itemConstraintsForPlan.constraint === 'specific' && itemConstraintsForPlan.item_price_ids.includes(planId));
  if (isDiscountApplicableToPlan) {
    return {
      isValid: true,
      ...coupon_result.data,
      code: couponCode
    };
  }
  return {
    isValid: false,
    code: couponCode,
    message: couponCodeErrorMessage
  };
};

const getCouponIds = (coupon, quantity) => {
  const { code, apply_on, discount_type, isValid } = coupon;

  // code should exist and must be valid before passing into chargebee
  // empty array means, no code
  if (!code || !isValid) {
    return [];
  }

  /**
   * If single quantity is ordered, one coupon is used
   * If coupon is to apply on invoice amount which is total, one coupon will be used
   * If discount type is percentage, one coupon will be used otherwise discount will be used multiple times on total amount of addons
   */
  if (quantity === 1 || apply_on === 'invoice_amount' || discount_type === 'percentage') {
    return [code];
  }
  /**
   * We can't use same coupon multiple times in a subscription, so we have manually generated 8 other clones of same coupon
   * and will be picked here based on number of quantities
   * todo: this code exists to temporarily support FREE_ADAPTER COUPON CODE for AU orders
   * remove this after GFRADAPTER is widely adopted
   */
  const codes = [code];
  for (let i = 2; i <= quantity; i += 1) {
    codes.push(`${code}_${i}`);
  }
  return codes;
};

export const mapToChargebeeSubscriptionRequestPayload = async (
  customer,
  subscription,
  products,
  coupon,
  shipment,
  paymentData,
  referral,
  paypalExpress = {}
) => {
  const subscriptionProducts = products.map((p) => {
    return { id: p.id, quantity: subscription.quantity };
  });
  // shipment has been created as a add-on in chargebee to include in the payment
  const shipmentAddOn = { id: shipment.id, quantity: 1 };
  const address = {
    first_name: customer.firstName,
    last_name: customer.lastName,
    company: customer.companyName,
    line1: customer.address,
    line2: customer.apartment,
    line3: customer.suburb,
    state: customer.state,
    city: customer.city,
    zip: customer.postalCode,
    country: customer.country
  };
  const subscriptionPayload = {
    ...paypalExpress,
    plan_id: subscription.plan.id,
    auto_collection: 'on',
    payment_intent: paymentData,
    plan_quantity: subscription.quantity,
    coupon_ids: [coupon.code],
    // Will be used later to get list of codes
    parentCode: coupon.parentCode,
    parentCodeApplyOn: coupon.apply_on,
    discountType: coupon.discount_type,
    cf_applied_referral_code: referral.code,
    cf_applied_referral_code_type: referral.type,
    // Chargebee Order reporter scheduler later uses this info to list all subscription made via website
    meta_data: {
      order_source: 'chargebee'
    },
    billing_address: {
      ...address,
      state_code: customer.stateCode
    },
    // as part of phase 1, we will use same address as both billing and shipping
    // todo: we will later allow user to set different addresses for them
    // do not send statecode to chargebee since it's enabled for selected countries only in chargebee
    // https://apidocs.chargebee.com/docs/api/addresses#address_state_code
    shipping_address: address,
    customer: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      email: customer.email,
      locale: 'en-US',
      company: customer.company,
      phone: customer.phone
    },
    addons: [...subscriptionProducts, shipmentAddOn]
  };
  return subscriptionPayload;
};

export const mapToChargebeeSubscriptionRequestPayloadV2 = async (
  customer,
  subscription,
  products,
  coupon,
  shipment,
  paymentData,
  referral,
  paypalExpress = {}
) => {
  const subscriptionProducts = products.map((p) => {
    return { item_price_id: p.id, quantity: subscription.quantity };
  });

  const subscriptionItems = [
    ...subscriptionProducts,
    {
      item_price_id: subscription.plan.id,
      quantity: subscription.quantity
    }
  ];
  if (shipment) {
    subscriptionItems.push({
      item_price_id: shipment.id,
      quantity: 1
    });
  }
  const coupon_ids = getCouponIds(coupon, subscription.quantity);
  const address = {
    first_name: customer.firstName,
    last_name: customer.lastName,
    email: customer.email,
    company: customer.companyName,
    phone: customer.phone,
    line1: customer.address,
    line2: customer.apartment,
    line3: customer.suburb,
    city: customer.city,
    state: customer.state,
    country: customer.country,
    zip: customer.postalCode
  };
  const subscriptionPayload = {
    plan_id: subscription.plan.id,
    auto_collection: 'on',
    payment_intent: paymentData,
    plan_quantity: subscription.quantity,
    coupon_ids,
    // Chargebee Order reporter scheduler later uses this info to list all subscription made via website
    cf_applied_referral_code: referral.code,
    cf_applied_referral_code_type: referral.type,
    billingAgreementId:
      paypalExpress.payment_method && paypalExpress.payment_method?.reference_id.length > 0
        ? paypalExpress.payment_method.reference_id
        : '',
    meta_data: {
      order_source: 'chargebee'
    },
    billing_address: {
      ...address,
      state_code: customer.stateCode
    },
    // as part of phase 1, we will use same address as both billing and shipping
    // todo: we will later allow user to set different addresses for them
    // do not send statecode to chargebee since it's enabled in chargebee for selected countries only
    // https://apidocs.chargebee.com/docs/api/addresses#address_state_code
    shipping_address: address,
    customer: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      email: customer.email,
      locale: 'en-US',
      company: customer.company,
      phone: customer.phone
    },
    subscription_items: subscriptionItems
  };
  return subscriptionPayload;
};

export const fetchPaypalCustomerDetail = async (token, customer) => {
  const paypal_customer_result = await gofarService(
    `/api/checkout/paypal-customer-data?token=${token}`,
    HTTP_METHODS.GET
  );
  if (paypal_customer_result.status !== HTTP_STATUS_CODES.SUCCESS) {
    return customer;
  }
  const paypalCustomer = paypal_customer_result.data;
  const stateName = STATES[paypalCustomer.countryCode][paypalCustomer.state];
  const gofarCustomer = {
    email: paypalCustomer.email.length > 0 ? decodeURIComponent(paypalCustomer.email) : customer.email,
    firstName: paypalCustomer.firstName.length > 0 ? decodeURIComponent(paypalCustomer.firstName) : customer.firstName,
    lastName: paypalCustomer.lastName.length > 0 ? decodeURIComponent(paypalCustomer.lastName) : customer.lastName,
    address:
      paypalCustomer.address_line1.length > 0 ? decodeURIComponent(paypalCustomer.address_line1) : customer.address,
    apartment:
      paypalCustomer.address_line2.length > 0 ? decodeURIComponent(paypalCustomer.address_line2) : customer.apartment,
    city: paypalCustomer.city.length > 0 ? decodeURIComponent(paypalCustomer.city) : customer.city,
    suburb: paypalCustomer.city.length > 0 ? decodeURIComponent(paypalCustomer.city) : customer.suburb,
    state: stateName && stateName.length > 0 ? stateName : customer.state,
    stateCode: paypalCustomer.state.length > 0 ? paypalCustomer.state : customer.stateCode,
    postalCode: paypalCustomer.zip && paypalCustomer.zip.length > 0 ? paypalCustomer.zip : customer.postalCode,
    country: paypalCustomer.countryCode.length > 0 ? paypalCustomer.countryCode : customer.country,
    phone: paypalCustomer.phone.length > 0 ? decodeURIComponent(paypalCustomer.phone) : customer.phone,
    // recently we have experience the issue where despite having legit paypal account, chargebee is not able to collect the payment
    // that led us to discover that the payer status can be verified/unverified, and if unverified it could return message like no valid card on file
    payerStatus: paypalCustomer.payerStatus
  };
  return gofarCustomer;
};

export const addRayToOrder = async (subscriptionId) => {
  await gofarService(`/api/checkout/chargebee/addchargetoorder`, HTTP_METHODS.POST, {
    subscription_id: subscriptionId
  });
};
